<script setup>
import { computed, nextTick, ref } from 'vue';
import EmojiPickerButton from './EmojiPickerButton.vue';

/**
 * Write and attach, for either end. The attachment limits come from the
 * server and are stated before anything is chosen, and a file that breaks
 * them is refused here with the reason rather than failing on upload.
 *
 * Depending on the configuration, what was written either leaves the box
 * immediately on send or stays until the server has it. A send that fails is
 * marked not sent, with the text and files kept or restored for a retry.
 */
const props = defineProps({
  placeholder: { type: String, default: '' },
  rules: { type: Object, default: () => ({ types: [], max_mb: 0, max_files: 0 }) },
  // Called with { body, files }; resolves once the message is sent, rejects if it was not.
  submit: { type: Function, required: true },
  // 'hub': the pill with the field inside it. 'portal': a bordered field between the two buttons.
  variant: { type: String, default: 'hub' },
  optimistic: { type: Boolean, default: false },
});

const draft = ref('');
const files = ref([]);
const problem = ref('');
const picker = ref(null);
const input = ref(null);
const pending = ref(false);
const failed = ref(false);
const reason = ref('');

const canSend = computed(() => !pending.value && (draft.value.trim() !== '' || files.value.length > 0));

const limits = computed(() => {
  const r = props.rules;
  if (!r.max_files) return '';

  return `Attach up to ${r.max_files} files, ${r.max_mb} MB each: ${r.types.map((t) => t.toUpperCase()).join(', ')}.`;
});

const extension = (name) => (name.includes('.') ? name.split('.').pop().toLowerCase() : '');

const refusal = (file) => {
  if (!props.rules.types.includes(extension(file.name))) return `${file.name} is not a type that can be sent.`;
  if (file.size > props.rules.max_mb * 1048576) return `${file.name} is larger than ${props.rules.max_mb} MB.`;

  return '';
};

const choose = (event) => {
  const chosen = [...(event.target.files || [])];
  event.target.value = '';
  problem.value = chosen.map(refusal).find(Boolean) || '';

  const accepted = chosen.filter((file) => !refusal(file));
  const room = props.rules.max_files - files.value.length;

  if (accepted.length > room) problem.value = `You can attach up to ${props.rules.max_files} files to one message.`;
  files.value = [...files.value, ...accepted.slice(0, Math.max(room, 0))];
};

const remove = (index) => files.value.splice(index, 1);

/** At the caret, not appended — "thanks 🙏" is written in the middle of a sentence as often as at its end. */
const insert = async (text) => {
  const el = input.value;
  const start = el.selectionStart ?? draft.value.length;
  const end = el.selectionEnd ?? start;
  draft.value = draft.value.slice(0, start) + text + draft.value.slice(end);
  await nextTick();
  el.focus();
  el.setSelectionRange(start + text.length, start + text.length);
};

const prefill = async (text) => {
  draft.value = text;
  await nextTick();
  input.value.focus();
  input.value.setSelectionRange(text.length, text.length);
};

defineExpose({ insert, prefill });

// Back to the field when the picker closes, unless the click that closed it landed in another one.
const refocus = () => {
  const active = document.activeElement;
  if (!active || active === document.body || active.closest('.pge-tooltip, .comp')) input.value?.focus();
};

// Typed or attached while it was sending: that is the next message, so it stays.
const clearSent = (message) => {
  if (draft.value.trim() === message.body) draft.value = '';
  files.value = files.value.filter((file) => !message.files.includes(file));
  problem.value = '';
};

const takeMessage = () => {
  const message = { body: draft.value.trim(), files: [...files.value] };
  if (props.optimistic) clearSent(message);

  return message;
};

const restoreOrKeep = (message) => {
  if (!props.optimistic) return;
  if (message.body) draft.value = draft.value ? `${message.body} ${draft.value}` : message.body;
  files.value = [...message.files, ...files.value];
};

const send = async () => {
  if (!canSend.value) return;
  const message = takeMessage();
  pending.value = true;
  failed.value = false;

  try {
    await props.submit(message);
    clearSent(message);
  } catch (e) {
    failed.value = true;
    // Only a refusal says something worth reading; anything else is just "not sent".
    reason.value = e?.response?.status === 422 ? e.response.data?.message || '' : '';
    restoreOrKeep(message);
  } finally {
    pending.value = false;
  }
};

// Enter sends; Shift+Enter is left to the field as a new line. Not while an IME is composing.
const onEnter = (event) => {
  if (event.isComposing) return;
  event.preventDefault();
  send();
};
</script>

<template>
  <div class="composer" :class="`composer--${props.variant}`">
    <ul v-if="files.length" class="composer__files" data-testid="composer-files">
      <li v-for="(file, index) in files" :key="file.name + index">
        {{ file.name }}
        <button type="button" :aria-label="`Remove ${file.name}`" @click="remove(index)">×</button>
      </li>
    </ul>

    <div class="comp" data-testid="composer">
      <button v-if="props.variant === 'portal'" type="button" class="rnd" aria-label="Attach a file" data-testid="attach" @click="picker.click()">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M21 11.5 12.5 20a5 5 0 0 1-7-7L14 4.5a3.3 3.3 0 0 1 4.7 4.7L10.2 17.7a1.7 1.7 0 0 1-2.4-2.4L15.5 7.6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" /></svg>
      </button>
      <div class="comp__grow" :data-value="draft">
        <textarea
          ref="input"
          v-model="draft"
          rows="1"
          class="comp__input"
          data-testid="thread-input"
          :placeholder="props.placeholder"
          :aria-label="props.placeholder"
          maxlength="4000"
          @keydown.enter.exact="onEnter"
        />
      </div>
      <button v-if="props.variant === 'hub'" type="button" class="rnd flat" aria-label="Attach a file" data-testid="attach" @click="picker.click()">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M21 11.5 12.5 20a5 5 0 0 1-7-7L14 4.5a3.3 3.3 0 0 1 4.7 4.7L10.2 17.7a1.7 1.7 0 0 1-2.4-2.4L15.5 7.6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" /></svg>
      </button>
      <EmojiPickerButton class="rnd emobtn" :class="{ flat: props.variant === 'hub' }" title="Emoji" aria-label="Emoji" data-testid="emoji" @pick="insert" @close="refocus">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" /><path d="M8.5 14.5c.9 1.2 2.1 1.8 3.5 1.8s2.6-.6 3.5-1.8" /><path d="M9 9.5h.01M15 9.5h.01" />
        </svg>
      </EmojiPickerButton>
      <button type="button" class="rnd p" aria-label="Send" data-testid="thread-send" :disabled="!canSend" @click="send">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12 20 4l-8 16-2-6-6-2Z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" /></svg>
      </button>
      <input
        ref="picker"
        type="file"
        multiple
        hidden
        aria-label="Files to attach"
        data-testid="file-picker"
        :accept="props.rules.types.map((t) => `.${t}`).join(',')"
        @change="choose"
      >
    </div>

    <p v-if="failed && !pending" role="alert" class="composer__failed" data-testid="send-failed">
      Not sent<template v-if="reason"> — {{ reason }}</template> ·
      <button type="button" data-testid="send-retry" :disabled="!canSend" @click="send">Retry</button>
    </p>
    <p v-if="problem" role="alert" class="composer__problem" data-testid="attach-problem">{{ problem }}</p>
    <p v-else-if="limits" class="composer__limits" data-testid="attach-limits">{{ limits }}</p>
  </div>
</template>

<style scoped lang="scss">
.comp {
  display: flex;
  gap: 9px;
  align-items: flex-end;
}

.composer--hub .comp {
  background: #fff;
  border: 1px solid var(--xl-color-border, #ede7f0);
  border-radius: 26px;
  padding: 6px 6px 6px 18px;
  box-shadow: var(--xl-shadow-1, 0 1px 2px rgba(28, 23, 32, 0.04));
}

// The field grows with its text by stacking it on an invisible copy of that
// text, so nothing is measured and the page never jumps: five lines, then it scrolls.
.comp__grow {
  flex: 1;
  min-width: 0;
  display: grid;

  &::after {
    content: attr(data-value) ' ';
    visibility: hidden;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    overflow: hidden;
  }

  > .comp__input,
  &::after {
    grid-area: 1 / 1 / 2 / 2;
    box-sizing: border-box;
    max-height: 122px;
    margin: 0;
    padding: 8.5px 0;
    border: 0;
    font: inherit;
    font-size: 15px;
    line-height: 21px;
  }
}

.comp__input {
  resize: none;
  overflow-y: auto;
  outline: none;
  background: none;
  color: var(--xl-color-primary-text, #1c1720);

  // The invisible copy holds the draft, not this, so a wrapped placeholder would be cut in half.
  &::placeholder { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
}

.composer--portal .comp { padding: 12px 16px; border-top: 1px solid var(--xl-color-border, #ede7f0); }

.composer--portal .rnd { margin-bottom: 2px; }

.composer--portal .comp__grow {
  > .comp__input,
  &::after {
    max-height: 126px;
    padding: 9.5px 16px;
    border: 1px solid transparent;
    border-radius: 21px;
  }

  > .comp__input {
    background: #fff;
    border-color: var(--xl-color-border, #ede7f0);
  }
}

.rnd {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  border: 0;
  padding: 0;
  border-radius: 50%;
  display: grid;
  place-items: center;
  cursor: pointer;
  font: inherit;
  background: var(--xl-color-primary-container, #f5eff9);
  color: var(--xl-color-primary, #5e2f78);

  &.p {
    background: linear-gradient(155deg, var(--xl-color-surface-tint, #8154a6), var(--xl-color-primary, #5e2f78));
    color: #fff;
  }

  &.flat { background: none; color: var(--xl-color-outline, #867d8d); }
  &.flat:hover { background: var(--xl-color-primary-container, #f5eff9); }
  &.emobtn:hover { color: var(--xl-color-primary, #5e2f78); }
  &.emobtn.on { background: var(--xl-color-primary-container, #f5eff9); color: var(--xl-color-primary, #5e2f78); }

  &:disabled { opacity: 0.45; cursor: default; }
  &:focus-visible { outline: 2px solid var(--xl-color-primary, #5e2f78); outline-offset: 2px; }
}

.composer__files {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 8px;
  padding: 0;

  li {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 26px;
    padding: 0 6px 0 10px;
    border-radius: 99px;
    background: var(--xl-color-primary-container, #f5eff9);
    color: var(--xl-color-primary, #5e2f78);
    font-size: 12.5px;
  }

  button { border: 0; background: none; color: inherit; cursor: pointer; font-size: 15px; line-height: 1; }
}

.composer--portal .composer__files { margin: 10px 16px 0; }

.composer__limits,
.composer__problem,
.composer__failed {
  margin: 8px 4px 0;
  font-size: 12px;
  color: var(--xl-color-outline, #867d8d);
}

.composer__problem,
.composer__failed { color: var(--xl-color-error, #a32020); }

.composer__failed button {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.composer--portal .composer__limits,
.composer--portal .composer__problem,
.composer--portal .composer__failed { margin: 0 16px 12px; }
</style>
